import { TableUI } from "components/design/TableUI";
import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import Modal from "components/DesignSystem/Modal/Modal";
import Table from "components/DesignSystem/Table/V2/Table";
import { Label } from "components/DesignSystem/TextInput/TextInput";
import { FormikForm } from "components/FormikForm/FormikForm";
import {
  BANK_CONNECTED_BROADCAST_MESSAGE,
  BANK_CONNECTION_SUCCESS_BROADCAST_CHANNEL,
} from "constants/connections";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useBroadcastChannel } from "hooks/useBroadcastChannel";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { Link, useHistory } from "react-router-dom";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import {
  bankConnectionsApi,
  useGetEntityBanksQuery,
} from "store/apis/bankConnections";
import * as Yup from "yup";
import { ImportCOAHeader } from "./ImportCOA";
import {
  useGetCOAImportStateQuery,
  useUpdateCOAMappingMutation,
} from "store/apis/chartOfAccounts";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Skeleton } from "components/DesignSystem/Skeleton/Skeleton";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";
import { RightArrow } from "components/icons/RightArrow";
import { ReactNode } from "react";
import { CSSObjectWithLabel } from "react-select";
import { useWindowSize } from "hooks/useWindowSize";
import { getMobileMenuPortalStyles } from "util/getMobileMenuPortalStyles";
import {
  IMPORT_SOURCE,
  IMPORT_SOURCE_LABEL,
  ImportSource,
} from "constants/qboImportSteps";

export type BankMapFormValues = {
  banks: BankMap[];
  cards: BankMap[];
};

const TABLE_TYPE = {
  banks: "banks",
  cards: "cards",
} as const satisfies Record<keyof BankMapFormValues, string>;

type TableType = (typeof TABLE_TYPE)[keyof typeof TABLE_TYPE];

type MapTableMeta = {
  tableType: TableType;
  shouldHideNoneOption: boolean;
};

type MapDataConnectionsStepProps = {
  onNext: () => void;
  importProcessId: string;
  entityId: string;
  header?: ReactNode;
  initialValues?: BankMapFormValues;
  onFormValuesChange?: (values: BankMapFormValues) => void;
  additionalFooterButtons?: ReactNode;
  source?: ImportSource;
  shouldHideNoneOption?: boolean;
};

export type BankMap = {
  name: string;
  source_id: string;
};

const MENU_WIDTH = 500;

const validationSchema = Yup.object().shape({
  banks: Yup.array().of(
    Yup.object().shape({
      name: Yup.string().required("Bank name is required"),
      source_id: Yup.string().required("Data connection is required"),
    })
  ),
  cards: Yup.array().of(
    Yup.object().shape({
      name: Yup.string().required("Credit card name is required"),
      source_id: Yup.string().required("Data connection is required"),
    })
  ),
});

const DataConnection = ({
  bank,
  banks,
  name,
  tableType,
  shouldHideNoneOption,
}: {
  bank: BankMap;
  banks: BankMap[];
  name: string;
  tableType: TableType;
  shouldHideNoneOption: boolean;
}) => {
  const {
    location: { search },
  } = useHistory();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { isMobile } = useWindowSize();
  const { data: banksAccounts } = useGetEntityBanksQuery(
    { entityId, groupId },
    { skip: !entityId || !groupId }
  );

  const allReadySelectedSources = banks
    .map((bank) => bank.source_id)
    .filter((id) => id !== "NONE");

  const bankAccounts =
    banksAccounts?.accounts.filter((acc) => !acc.is_credit_account) || [];

  const creditCardAccounts =
    banksAccounts?.accounts.filter((acc) => acc.is_credit_account) || [];

  const banksOptions =
    tableType === TABLE_TYPE.banks ? bankAccounts : creditCardAccounts;

  const noneOption = {
    label: (
      <>
        None{" "}
        <i className="t-text-text-30">
          (The category is not linked to a bank account)
        </i>
      </>
    ),
    value: "NONE",
  };

  const accountOptions = banksOptions.map(
    ({ account: { mask, nickname, uuid } }) => ({
      value: uuid,
      label: `${nickname}  ${mask ? `- ${mask}` : ""}`,
      isDisabled: allReadySelectedSources.includes(uuid),
    })
  );

  const options = shouldHideNoneOption
    ? accountOptions
    : [noneOption, ...accountOptions];

  const selectedBank = options.find(({ value }) => value === bank.source_id);

  const getMenuPortalStyles = (
    base: CSSObjectWithLabel
  ): CSSObjectWithLabel => {
    if (isMobile) return getMobileMenuPortalStyles(base, MENU_WIDTH);
    return { ...base, width: MENU_WIDTH };
  };

  return (
    <Combobox
      size="small"
      options={options}
      value={selectedBank}
      name={name}
      withForm
      actions={
        <Link
          target="_blank"
          to={{
            pathname: `/books/data-sources${search}&modal=BANK_CONNECTION_MODAL`,
          }}
        >
          <Button customType="link">Add data source</Button>
        </Link>
      }
      menuPortalTarget={document.body}
      styles={{
        menuPortal: getMenuPortalStyles,
      }}
    />
  );
};

const columnHelper = createColumnHelper<BankMap>();

export const columns = [
  columnHelper.accessor("name", {
    header: (info) => {
      const tableType = (info.table.options.meta as MapTableMeta)?.tableType;

      return (
        <Label>
          Imported{" "}
          {tableType === TABLE_TYPE.banks ? "bank accounts" : "credit cards"}
        </Label>
      );
    },
    size: 50,
    cell: (info) => (
      <div className="t-text-body-sm t-text-text-100 t-flex t-items-center t-gap-2 t-justify-between">
        {info.getValue()}
        <span className="t-text-neutral-30">
          <RightArrow />
        </span>
      </div>
    ),
  }),
  columnHelper.accessor("source_id", {
    header: () => <Label required>Data connection</Label>,
    size: 50,
    cell: (info) => {
      const { tableType, shouldHideNoneOption } = info.table.options
        .meta as MapTableMeta;

      return (
        <DataConnection
          name={`${tableType}.${info.row.index}.source_id`}
          bank={info.row.original}
          banks={info.table.options.data}
          tableType={tableType}
          shouldHideNoneOption={shouldHideNoneOption}
        />
      );
    },
  }),
];

const MapTable = ({
  data,
  tableType,
  shouldHideNoneOption,
}: {
  data: BankMap[];
  tableType: TableType;
  shouldHideNoneOption: boolean;
}) => {
  const meta: MapTableMeta = { tableType, shouldHideNoneOption };

  const table = useReactTable({
    columns: columns,
    data: data,
    getCoreRowModel: getCoreRowModel(),
    meta,
  });

  return (
    <div className="t-border-neutral-0 t-border-solid t-border t-text-text-60 t-rounded-lg t-px-2.5 t-pt-2.5">
      <TableUI
        table={table}
        contentClassName="sm:t-min-w-[600px]"
        size="small"
        sticky={false}
        header={({ headerGroup }) => (
          <Table.Row>
            {headerGroup.headers.map((header) => {
              return (
                <Table.HeadCell
                  colSpan={header.colSpan}
                  isUpperCase={false}
                  key={header.id}
                  style={{
                    width: `${header.getSize()}%`,
                    minWidth: `${header.column.getSize()}%`,
                  }}
                  className="t-rounded-lg"
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </Table.HeadCell>
              );
            })}
          </Table.Row>
        )}
      />
    </div>
  );
};

export const MapDataConnectionsStepContent = ({
  onNext,
  entityId,
  importProcessId,
  header,
  initialValues: providedInitialValues,
  onFormValuesChange,
  additionalFooterButtons,
  source = IMPORT_SOURCE.QBO,
  shouldHideNoneOption = false,
}: Omit<MapDataConnectionsStepProps, "header"> & { header: ReactNode }) => {
  const dispatch = useAppDispatch();
  const { alertToast } = useToast();
  const [updateBankAccountMap] = useUpdateCOAMappingMutation();
  const { data, isLoading, isSuccess } = useGetCOAImportStateQuery(
    { entityId, coaImportStateId: importProcessId, review: false },
    { skip: !entityId || !importProcessId }
  );
  const { bank_account_names = [], credit_card_names = [] } = data || {};

  const { close: closeChannel } = useBroadcastChannel(
    BANK_CONNECTION_SUCCESS_BROADCAST_CHANNEL,
    (event, channel) => {
      if (event.data === BANK_CONNECTED_BROADCAST_MESSAGE) {
        dispatch(
          bankConnectionsApi.util.invalidateTags([{ type: "CONNECTION" }])
        );
      }
    }
  );

  const handleSubmit = async (values: BankMapFormValues) => {
    try {
      const bankAccountMap = [...values.banks, ...values.cards];
      await updateBankAccountMap({
        entityId,
        coaImportStateId: importProcessId,
        bankAccountMap,
      }).unwrap();

      if (onFormValuesChange) {
        onFormValuesChange(values);
      }

      closeChannel();
      onNext();
    } catch (error) {
      alertToast(
        { message: (error as BackendError).data?.error?.message },
        error as Error
      );
    }
  };

  const banksList = bank_account_names.map((name) => ({ name, source_id: "" }));
  const cardsList = credit_card_names.map((name) => ({ name, source_id: "" }));

  const defaultInitialValues = {
    banks: banksList,
    cards: cardsList,
  };

  const initialValues = providedInitialValues || defaultInitialValues;

  const noMapFoundNext = () => {
    closeChannel();
    onNext();
  };

  return (
    <FormikForm<BankMapFormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ submitForm, values: { banks, cards }, isSubmitting }) => (
        <>
          {header}
          <Async.Root
            isLoading={isLoading}
            isSuccess={isSuccess}
            isEmpty={
              bank_account_names.length === 0 && credit_card_names.length === 0
            }
            renderOnLoad={
              <Modal.Body className="t-flex t-flex-col t-gap-2 sm:t-px-4">
                <Skeleton className="t-h-44 t-rounded-lg" />
                <Skeleton className="t-h-44 t-rounded-lg" />
              </Modal.Body>
            }
          >
            <Async.Empty>
              <>
                <Modal.Body className="t-text-center t-h-60 t-text-body-sm t-text-text-100 t-flex t-items-center t-justify-center sm:t-px-4">
                  No banks or credit cards found in the uploaded Chart of
                  Accounts file.
                </Modal.Body>
                <Modal.FooterButtonGroup>
                  {additionalFooterButtons}
                  <Modal.RawClose asChild>
                    <Button type="reset">Cancel</Button>
                  </Modal.RawClose>
                  <Button
                    customType="primary"
                    type="button"
                    onClick={noMapFoundNext}
                  >
                    Next
                  </Button>
                </Modal.FooterButtonGroup>
              </>
            </Async.Empty>
            <Async.Success>
              <Modal.Body className="t-flex t-flex-col t-gap-2 t-overflow-auto t-max-h-[60vh] sm:t-px-4">
                <p className="t-text-body-sm t-text-text-100">
                  Map banks and credit cards imported from{" "}
                  {IMPORT_SOURCE_LABEL[source]} to data connections in Inkle
                  Books
                </p>
                <div className="t-flex t-flex-col t-gap-8">
                  <div>
                    <p className="t-text-subtext t-text-text-100 t-px-2 t-mb-2">
                      Banks
                    </p>
                    <MapTable
                      data={banks}
                      tableType={TABLE_TYPE.banks}
                      shouldHideNoneOption={shouldHideNoneOption}
                    />
                  </div>
                  <div>
                    <p className="t-text-subtext t-text-text-100 t-px-2 t-mb-2">
                      Credit cards
                    </p>
                    <MapTable
                      data={cards}
                      tableType={TABLE_TYPE.cards}
                      shouldHideNoneOption={shouldHideNoneOption}
                    />
                  </div>
                </div>
              </Modal.Body>
              <Modal.FooterButtonGroup>
                {additionalFooterButtons}
                <Modal.RawClose asChild>
                  <Button type="reset" disabled={isSubmitting}>
                    Cancel
                  </Button>
                </Modal.RawClose>
                <Button
                  customType="primary"
                  disabled={isSubmitting}
                  isLoading={isSubmitting}
                  onClick={submitForm}
                >
                  Next
                </Button>
              </Modal.FooterButtonGroup>
            </Async.Success>
          </Async.Root>
        </>
      )}
    </FormikForm>
  );
};

export const MapDataConnectionsStep = ({
  onNext,
  entityId,
  importProcessId,
  header,
  initialValues,
  onFormValuesChange,
  additionalFooterButtons,
}: MapDataConnectionsStepProps) => {
  return (
    <Modal.Content size="xl" useCustomOverlay>
      <MapDataConnectionsStepContent
        onNext={onNext}
        entityId={entityId}
        importProcessId={importProcessId}
        header={header || <ImportCOAHeader />}
        initialValues={initialValues}
        onFormValuesChange={onFormValuesChange}
        additionalFooterButtons={additionalFooterButtons}
      />
    </Modal.Content>
  );
};
